import { Divider } from '@mui/material';
import { Row, Table } from '@tanstack/react-table';
import { L } from 'harmony-language';
import React from 'react';
import { OrgQueryKeys } from '../../../../api/config';
import { useOrganizationQuery } from '../../../../api/queries/use-organization-query';
import { Customer, Load, OrganizationLocation } from '../../../../types';
import { GlobalFilter, GlobalFilterObject } from '../../hooks/use-draft-load-table';
import { DraftLoadFilter } from './draft-load-filter';

export type CustomerFilter = GlobalFilterObject<'customerFilter', number[]>;

interface CustomerFilterProps {
    table: Table<Load>;
}

export const CustomerFilter: React.FC<CustomerFilterProps> = (props) => {
    const { table } = props;
    const { data: organizationLocations = [] } = useOrganizationQuery<OrganizationLocation[]>(OrgQueryKeys.locations);
    const { data: customers = [] } = useOrganizationQuery<Customer[]>(OrgQueryKeys.customers);
    
    const loads = table.getRowModel().rows.map(row => row.original);
    const globalFilter = table.getState().globalFilter as GlobalFilter;
    const currentCustomerFilters = globalFilter?.filter(filter => filter.type === 'customerFilter');
    const selectedCustomerIds = organizationLocations
        .filter(orgLoc => currentCustomerFilters?.some(filter => filter.value.includes(orgLoc.id)))
        .map(orgLoc => orgLoc.customerId)
        .filter(id => id !== null);

    const customerIdsInTable = loads.flatMap(load => load.stops
        .map(stop => organizationLocations.find(orgLoc => orgLoc.id === stop.organizationLocationId)?.customerId)
        .filter(id => id !== null && id !== undefined)
    );
    const uniqueCustomerIds = [...new Set(customerIdsInTable)];
    const facetedCustomers = uniqueCustomerIds.map(id => customers.find(customer => customer.id === id)).filter(x => !!x);

    const customerLocationsFilterFn = (row: Row<Load>, orgLocationIds: number[]) => {
        const load = row.original;
        return load.stops.some(stop => orgLocationIds.includes(stop.organizationLocationId));
    };

    const handleCustomerChange = (customerId: number) => {
        const customerOrganizationLocationIds = organizationLocations
            .filter(x => x.customerId === customerId)
            .map(x => x.id);
        
        table.setGlobalFilter((prevGlobalFilter: GlobalFilter): GlobalFilter => {
            const hasNewValues = prevGlobalFilter?.some(
                filterItem => filterItem.type === 'customerFilter' && filterItem.value.some(
                    orgLocId => customerOrganizationLocationIds.includes(orgLocId)
                )
            );

            if (hasNewValues) {
                return prevGlobalFilter?.filter(filterItem => !(filterItem.type === 'customerFilter'
                    && filterItem.value.length === customerOrganizationLocationIds.length
                    && filterItem.value.every(orgLocId => customerOrganizationLocationIds.includes(orgLocId))
                ));
            } else {
                return [
                    ...(prevGlobalFilter || []), {
                        type: 'customerFilter',
                        label: customers.find(cust => cust.id === customerId)?.customerName || '',
                        value: customerOrganizationLocationIds,
                        filterFn: customerLocationsFilterFn,
                    },
                ];
            }
        })
    }

    return facetedCustomers.length > 0 && (
        <>
            <Divider />
            <DraftLoadFilter
                label={L.customers()}
                items={facetedCustomers}
                selectedIds={selectedCustomerIds}
                itemLabelFn={item => item.customerName}
                onChange={handleCustomerChange}
            />
        </>
    );
};
